import React, { useMemo } from 'react';
import { Button, Drawer, Empty, Input, Select, Space, Spin, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatDocumentUiDate } from '../../../../utils/documentUiFormat';
import { DataTable } from '../../../../ui';
import { useGetDocumentsRegistryQuery } from '../../../../store/api/documentRegistryApi';
import { filterDocumentsForCompositionPicker } from '../../lib/compositionDocumentPickerFilter';
import type { DocumentSelectionRequest, SelectedDocument } from '../../lib/documentSelectionTypes';
import { documentMatchesSearchQuery } from '../../../../utils/documentSearch';
import DocumentStatusBadge from '../../../../components/documents/DocumentStatusBadge';

const { Text } = Typography;

export type DocumentPickerDrawerProps = {
  open: boolean;
  request: DocumentSelectionRequest | null;
  onSelect: (document: SelectedDocument) => void;
  onCancel: () => void;
};

/**
 * Universal document picker — lists selectable documents and returns a choice.
 * Domain-agnostic: no Replace / Manual / Initialize semantics.
 */
const DocumentPickerDrawer: React.FC<DocumentPickerDrawerProps> = ({
  open,
  request,
  onSelect,
  onCancel,
}) => {
  const [search, setSearch] = React.useState('');
  const [typeFilterLabel, setTypeFilterLabel] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (!open) {
      setSearch('');
      setTypeFilterLabel(undefined);
    }
  }, [open]);

  const { data, isLoading, isFetching } = useGetDocumentsRegistryQuery(
    {
      applicationId: request?.applicationId,
      limit: 100,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    },
    { skip: !open || request == null }
  );

  const documents = useMemo(() => {
    if (!request) {
      return [];
    }
    return filterDocumentsForCompositionPicker(data?.documents ?? [], request);
  }, [data?.documents, request]);

  const documentTypeOptions = useMemo(
    () =>
      Array.from(new Set(documents.map((document) => document.typeLabel)))
        .filter((label) => label && label !== '—')
        .sort((a, b) => a.localeCompare(b, 'ru'))
        .map((label) => ({ value: label, label })),
    [documents]
  );

  const visibleDocuments = useMemo(() => {
    return documents.filter((document) => {
      if (typeFilterLabel && document.typeLabel !== typeFilterLabel) {
        return false;
      }
      return documentMatchesSearchQuery(
        {
          typeLabel: document.typeLabel,
          fileName: document.fileName,
          customDocumentName: document.customDocumentName,
          comment: document.comment,
          documentType: document.documentType,
        },
        search
      );
    });
  }, [typeFilterLabel, documents, search]);

  const columns: ColumnsType<SelectedDocument> = useMemo(
    () => [
      {
        title: 'Наименование',
        dataIndex: 'fileName',
        key: 'fileName',
        width: 180,
        ellipsis: true,
      },
      {
        title: 'Тип',
        dataIndex: 'typeLabel',
        key: 'typeLabel',
        width: 140,
        ellipsis: true,
      },
      {
        title: 'Номер',
        dataIndex: 'documentNumber',
        key: 'documentNumber',
        width: 100,
        ellipsis: true,
        render: (value: string | null) => value || '—',
      },
      {
        title: 'Дата',
        dataIndex: 'documentDate',
        key: 'documentDate',
        width: 105,
        render: (value: string) => formatDocumentUiDate(value),
      },
      {
        title: 'Статус',
        dataIndex: 'statusLabel',
        key: 'statusLabel',
        width: 110,
        render: (_value, record) => (
          <DocumentStatusBadge status={record.lifecycleState ?? record.statusLabel} />
        ),
      },
      {
        title: 'Контрагент',
        dataIndex: 'counterpartyLabel',
        key: 'counterpartyLabel',
        width: 150,
        ellipsis: true,
        render: (value: string | null) => value || '—',
      },
      {
        title: 'Маршрут / Заявка',
        key: 'scope',
        width: 180,
        ellipsis: true,
        render: (_value, record) =>
          record.routeLabel ?? record.applicationLabel ?? '—',
      },
      {
        title: '',
        key: 'action',
        width: 120,
        align: 'right',
        render: (_value, record) => (
          <Tooltip title={record.disabledReason ?? undefined}>
            <Button
              type="primary"
              size="small"
              disabled={Boolean(record.disabledReason)}
              onClick={() => {
                if (!record.disabledReason) {
                  onSelect(record);
                }
              }}
            >
              {record.disabledReason ? 'В комплекте' : 'Выбрать'}
            </Button>
          </Tooltip>
        ),
      },
    ],
    [onSelect]
  );

  const loading = isLoading || isFetching;

  return (
    <Drawer
      title="Выбор документа"
      open={open}
      onClose={onCancel}
      width={1100}
      destroyOnClose
      data-testid="document-picker-drawer"
    >
      {request ? (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">
            {request.selectionScope === 'manual' ? 'Контекст' : 'Слот'}:{' '}
            <Text strong>{request.slotLabel}</Text>
          </Text>
          <Space wrap size={8} style={{ width: '100%' }}>
            <Input.Search
              allowClear
              placeholder="Поиск по документам"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ width: 360 }}
            />
            <Select
              allowClear
              placeholder="Все типы документов"
              value={typeFilterLabel}
              options={documentTypeOptions}
              onChange={setTypeFilterLabel}
              style={{ width: 240 }}
            />
          </Space>
          <Spin spinning={loading}>
            <DataTable
              size="small"
              rowKey="documentId"
              columns={columns}
              dataSource={visibleDocuments}
              pagination={false}
              scroll={{ y: 420 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      loading
                        ? 'Загрузка документов…'
                        : 'Нет подходящих документов для выбора'
                    }
                  />
                ),
              }}
            />
          </Spin>
        </Space>
      ) : null}
    </Drawer>
  );
};

export default DocumentPickerDrawer;
